import { useEffect, useRef } from 'react'

/**
 * Cloudflare's widget, loaded only when a site key exists.
 *
 * Rendered explicitly rather than by their auto-scan, so it cannot attach to
 * the wrong element on a page that re-renders. Absent in development, where
 * the server skips the check — the alternative is a third-party script on
 * every local page load to prove a laptop is not a robot.
 */
export function Turnstile({
  siteKey,
  onToken,
}: {
  siteKey: string
  onToken: (token: string) => void
}) {
  const holder = useRef<HTMLDivElement>(null)
  const rendered = useRef(false)

  useEffect(() => {
    if (rendered.current || !holder.current) return
    rendered.current = true

    const mount = () => {
      const turnstile = (window as unknown as { turnstile?: {
        render: (el: HTMLElement, opts: Record<string, unknown>) => void
      } }).turnstile
      if (!turnstile || !holder.current) return
      turnstile.render(holder.current, { sitekey: siteKey, callback: onToken })
    }

    const existing = document.querySelector('script[data-turnstile]')
    if (existing) {
      mount()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.dataset.turnstile = 'true'
    script.onload = mount
    document.head.appendChild(script)
  }, [siteKey, onToken])

  return <div ref={holder} />
}
